import { useEffect, RefObject } from 'react';

/**
 * Call a callback when the user clicks or taps outside of an element. Based on https://usehooks.com/useOnClickOutside.
 * @param ref A reference to an element
 * @param handler The callback. This will rerender on every update unless the handler is wrapped in a useCallback;
 */
export const useClickOutside = <T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
  handler: (event: Event) => void
): void => {
  useEffect(() => {
    const listener = (event: Event) => {
      // Do nothing if clicking ref's element or descendent elements
      if (!ref?.current || ref.current.contains(event.target as Node) || null) {
        return;
      }

      handler(event);
    };

    document.addEventListener('click', listener);

    return () => {
      document.removeEventListener('click', listener);
    };
  }, [ref, handler]);
};
